import { useState, useEffect } from 'react';
import School from '../models/School';

// Shared cache + request de-duplication behind useSchools and useHSSchools.
//
// Three problems this exists to solve:
//
// 1. Every consumer fired its OWN request for the school list. A page holding
//    both a form and a SchoolInput fetched the same list twice, and the two
//    copies landed at different moments -- which is how a school picked from
//    the (already populated) dropdown could be missing from the form's still
//    empty copy, leaving it reporting "School is required" over a filled-in
//    control.
//
// 2. The localStorage cache was written on every fetch but read by only one of
//    the two hooks, so a returning visitor waited on the network for a list
//    that was already sitting on disk.
//
// 3. Both hooks used the SAME 'schools' key while fetching DIFFERENT sets --
//    useHSSchools returns high schools only. Whichever resolved last
//    overwrote the other, so a hook reading that cache could be handed the
//    wrong set. Each hook now owns a distinct, versioned key, and neither
//    reads the legacy one.

// Rehydrated lists, parsed once per page load and shared by every consumer.
// Sharing the array also keeps its identity stable between renders.
const parsedByKey = {};
// In-flight request per key, so simultaneous mounts share one round trip.
const inFlightByKey = {};

// The pre-split key held whichever set resolved last, so it is never safe to
// read. Drop it once on load -- a full school list runs to megabytes and
// localStorage quota is finite.
try {
  localStorage.removeItem('schools');
} catch (err) {
  // Private browsing / disabled storage. Nothing to reclaim, nothing to do.
}

export const cachedSchools = (cacheKey) => {
  if (parsedByKey[cacheKey]) return parsedByKey[cacheKey];

  try {
    const raw = localStorage.getItem(cacheKey);
    if (!raw) return [];

    const attrsList = JSON.parse(raw);
    if (!Array.isArray(attrsList)) return [];

    parsedByKey[cacheKey] = attrsList.map((attrs) => new School(attrs));
    return parsedByKey[cacheKey];
  } catch (err) {
    // A corrupt or unreadable cache is not worth taking the page down for --
    // the fetch below will repopulate it.
    return [];
  }
};

export const loadSchools = (cacheKey, buildScope) => {
  if (!inFlightByKey[cacheKey]) {
    inFlightByKey[cacheKey] = buildScope()
      .all()
      .then(({ data }) => {
        try {
          localStorage.setItem(cacheKey, JSON.stringify(data));
        } catch (err) {
          // Quota exceeded, or storage disabled. The cache is only ever an
          // optimisation, so a failed write must not fail the load.
        }
        parsedByKey[cacheKey] = data;
        return data;
      })
      .catch((err) => {
        // Drop the rejected promise so a later mount retries, rather than
        // every consumer inheriting one failure for the life of the page.
        delete inFlightByKey[cacheKey];
        throw err;
      });
  }

  return inFlightByKey[cacheKey];
};

// `buildScope` must be defined at module scope in the calling hook so its
// identity is stable across renders.
export const useSchoolList = (cacheKey, buildScope) => {
  const [schools, setSchools] = useState(() => cachedSchools(cacheKey));

  useEffect(() => {
    let active = true;

    loadSchools(cacheKey, buildScope)
      .then((data) => {
        if (active) setSchools(data);
      })
      .catch(() => {
        // Keep whatever the cache gave us rather than blanking the list.
      });

    return () => { active = false; };
  }, [cacheKey, buildScope]);

  return schools;
};

// Test seam: the module-level caches outlive a single test otherwise.
export const resetSchoolListCaches = () => {
  Object.keys(parsedByKey).forEach((k) => delete parsedByKey[k]);
  Object.keys(inFlightByKey).forEach((k) => delete inFlightByKey[k]);
};
